import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChevronRight, SkipForward, ArrowLeft, History } from "lucide-react";
import { cn } from "@/lib/utils";

interface PreviousAttemptsStepProps {
  initialValue: string;
  onNext: (value: string) => void;
  onSkip: () => void;
  onBack: () => void;
}

export const PreviousAttemptsStep = ({ initialValue, onNext, onSkip, onBack }: PreviousAttemptsStepProps) => {
  const [value, setValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && value.trim()) {
        onNext(value);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [value, onNext]);

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="space-y-3">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <History className="h-5 w-5" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Schritt 3 von 4</span>
            <span className="px-2 py-0.5 rounded-full bg-muted text-xs text-muted-foreground">Optional</span>
          </div>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold font-serif leading-tight text-foreground">
          Was habt ihr bisher unternommen?
        </h2>
        <p className="text-muted-foreground text-base">
          Gib an, welche Maßnahmen, Tests oder Gespräche bereits stattgefunden haben, die für diese Entscheidung relevant sind.
        </p>
      </div>

      <div className="space-y-3">
        <div className={cn(
          "relative rounded-xl border bg-card/50 backdrop-blur-sm transition-all duration-300 overflow-hidden",
          isFocused ? "border-primary shadow-[0_0_20px_hsl(var(--primary)/0.1)]" : "border-border/60"
        )}>
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="Beispiel: Pilotgespräche mit potenziellen Nutzern geführt, interne Test-Sessions mit realen Entscheidungen durchgeführt, erste Reports generiert und intern diskutiert."
            className="min-h-[160px] text-base resize-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
            autoFocus
          />
        </div>
        
        <div className="flex items-center justify-end text-sm text-muted-foreground">
          <span className="text-xs opacity-60">⌘ + Enter zum Fortfahren</span>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          onClick={onBack}
          className="gap-2 h-12 px-6 rounded-xl border-border/60 hover:bg-card hover:border-primary/40"
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück
        </Button>
        <Button
          variant="ghost"
          onClick={onSkip}
          className="gap-2 h-12 px-6 rounded-xl hover:bg-muted/50"
        >
          <SkipForward className="h-4 w-4" />
          Überspringen
        </Button>
        <Button
          onClick={() => onNext(value)}
          disabled={value.trim().length < 10}
          className="flex-1 gap-2 h-12 rounded-xl"
        >
          Weiter
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
